import { numericalControlVariableDefinition } from 'libs/end-to-end/src/control-variable-definition-factory';
import { numericalTraitDefinition } from 'libs/end-to-end/src/control-variable-definition-factory';
import { SuccessPhenotypeV2Request } from 'libs/end-to-end/src/custom-viz/SuccessPhenotypeTypes';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { CategoricalTraitLevel } from 'libs/end-to-end/src/success-phenotype';
import { SuccessPhenotypeBubbleGraphDatum } from 'libs/end-to-end/src/success-phenotype';
import { SuccessPhenotypeV2Result } from 'libs/end-to-end/src/success-phenotype';
import { ControlVariableDefinitionsMapping } from 'libs/end-to-end/src/success-phenotype';
import { Trait } from 'libs/end-to-end/src/success-phenotype';
import { TraitLevel } from 'libs/end-to-end/src/success-phenotype';
import { NumericalOneSidedTraitLevel } from 'libs/end-to-end/src/success-phenotype';
import { NumericalTraitLevel } from 'libs/end-to-end/src/success-phenotype';
import { NumericalTwoSidedTraitLevel } from 'libs/end-to-end/src/success-phenotype';
import { ControlVariableDefinition } from 'libs/end-to-end/src/types/databases/controls/control-variable';
import { NumericalTraitDefinition } from 'libs/end-to-end/src/types/databases/controls/control-variable';
import { getMetricByColumnHeaderValueFromObservation } from 'libs/end-to-end/src/me';
import { getMetricDisplayNameFromObservation } from 'libs/end-to-end/src/me';
import { TraitLevelBarChartDatum } from 'libs/end-to-end/src/trait-level-graph';
import { BinScatterMultiSelectRequest } from 'libs/end-to-end/src/custom-viz/BinScatterMultiSelectTypes';
import { sortWithReferenceOrderPlacingUnknownsLastAlphabetically } from 'libs/end-to-end/src/generic';
import { ExplanationsOutputDictionary } from 'libs/end-to-end/src/explanations';
import { getKeyFactorsBreakdownOutputKey } from 'libs/end-to-end/src/explanations-llm-types';
import { getKeyFactorsExampleOutputKey } from 'libs/end-to-end/src/explanations-llm-types';
import { getKeyFactorsOptimalRuleOutputKey } from 'libs/end-to-end/src/explanations-llm-types';
import { getKeyFactorsRuleOutputKey } from 'libs/end-to-end/src/explanations-llm-types';
import { MetricDefinition } from 'libs/end-to-end/src/types/databases/metrics/metric-definition';
import { upperFirst } from 'lodash';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { getAdditionalPerformanceFocusAreaControlVariableDefinitions } from 'libs/end-to-end/src/utils/databases/hardcoded-control-variables';
import { CustomVizPollCompleteResponse } from 'libs/end-to-end/src/custom-viz/CustomVizDefinitions';

export const getBinScatterMultiSelectPrimaryFilterSelections = ({
  request,
}: {
  request: Pick<BinScatterMultiSelectRequest, 'inputs'>;
}): PrimaryFilterSelection[] => {
  const extras = request.inputs.extras;

  const { metric, dimension } = extras;

  return [
    {
      primaryFilterKey: 'metric',
      value: metric,
    },
    {
      primaryFilterKey: 'dimension',
      value: dimension,
    },
  ];
};

export const getSuccessFactorsPrimaryFilterSelections = ({
  request,
}: {
  request: Pick<SuccessPhenotypeV2Request, 'inputs'>;
}): PrimaryFilterSelection[] => {
  const extras = request.inputs.extras;

  const { metric } = extras;

  return [
    {
      primaryFilterKey: 'focusArea',
      value: ['Performance', metric],
    },
  ];
};

export const isCategoricalTraitLevel = (
  traitLevel: TraitLevel,
): traitLevel is CategoricalTraitLevel => {
  return traitLevel.type === 'equal';
};

export const traitsToBubbleGraphDatums = (
  traits: Trait[],
): SuccessPhenotypeBubbleGraphDatum[] => {
  return traits.map((trait) => {
    return {
      bestTraitLevel: getBestTraitLevel(trait),
      trait,
    };
  });
};

/**
 * Gets the performance focus areas control variable definition mappings and puts them into an
 * object with the columnHeaderValue as the key, and the control variable definition as the value.
 *
 * It's used for easy lookup of the configured values to get the control variable's display name from the columnHeaderValue in the trait
 */
export const getControlVariableDefinitionsMapping = ({
  myObservation,
}: {
  myObservation: MyObservation | null;
}): ControlVariableDefinitionsMapping => {
  if (!myObservation) {
    return {};
  }

  const controlVariables = myObservation.focusAreas.find(
    (f) => f.focusArea === 'Performance',
  )?.controlVariables;

  if (!controlVariables) {
    return {};
  }

  const preferredMetric = myObservation.focusAreas
    .find((f) => f.focusArea === 'Performance')
    ?.metrics.find((m) => m.isPreferred);

  /**
   * These two values are hardcoded because they can't currently be set in the admin panel.
   * There is a bug that happens when the Python code creates `imputed_experience` when the `experience` control variable defintion is set in the admin panel
   * We're hardcoding them here so that we can show the display name, etc. when the successPhenotypeV2 custom viz returns a trait with either of these defintions even though it's not coming from the observation
   */
  const controlVariableDefinitionsMapping: ControlVariableDefinitionsMapping =
    {};

  const additionalControlVariables =
    getAdditionalPerformanceFocusAreaControlVariableDefinitions({
      preferredMetricColumnHeaderValue:
        preferredMetric?.columnHeaderValue ?? null,
      preferredMetricDisplayName: preferredMetric?.displayName ?? null,
    });

  for (const hardcodedControlVariable of additionalControlVariables) {
    controlVariableDefinitionsMapping[
      hardcodedControlVariable.columnHeaderValue
    ] = hardcodedControlVariable;
  }

  for (const controlVariable of controlVariables) {
    controlVariableDefinitionsMapping[controlVariable.columnHeaderValue] =
      controlVariable;
  }

  return controlVariableDefinitionsMapping;
};

/**
 * Gets the trait level with the highest percentage within a given trait. Higher percentage means more impactful
 */
export const getBestTraitLevel = (trait: Trait): TraitLevel => {
  return trait.levels.reduce((bestLevel, level) => {
    return level.percentage > bestLevel.percentage ? level : bestLevel;
  }, trait.levels[0]);
};

export const getSuccessPhenotypeV2ControlVariableDefinitionsMappingFromObservation =
  ({
    myObservation,
  }: {
    myObservation: MyObservation | undefined;
  }): ControlVariableDefinitionsMapping => {
    if (!myObservation) {
      return {};
    }

    const controlVariables = myObservation.focusAreas.find(
      (f) => f.focusArea === 'Performance',
    )?.controlVariables;

    if (!controlVariables) {
      return {};
    }

    /**
     * These two values are hardcoded because they can't currently be set in the admin panel.
     * There is a bug that happens when the Python code creates `imputed_experience` when the `experience` control variable defintion is set in the admin panel
     * We're hardcoding them here so that we can show the display name, etc. when the successPhenotypeV2 custom viz returns a trait with either of these defintions even though it's not coming from the observation
     */
    const controlVariableDefinitionsMapping: ControlVariableDefinitionsMapping =
      {
        experience: numericalControlVariableDefinition({
          columnHeaderValue: 'experience',
          displayName: 'Experience',
          numericalTraitDefinition: numericalTraitDefinition({
            unitPlural: 'years',
            unitSingular: 'year',
            denominator: 0.01,
          }),
        }),
        tenure: numericalControlVariableDefinition({
          columnHeaderValue: 'tenure',
          displayName: 'Tenure',
          numericalTraitDefinition: numericalTraitDefinition({
            unitPlural: 'years',
            unitSingular: 'year',
            denominator: 0.01,
          }),
        }),
      };

    for (const controlVariable of controlVariables) {
      controlVariableDefinitionsMapping[controlVariable.columnHeaderValue] =
        controlVariable;
    }

    return controlVariableDefinitionsMapping;
  };

export const getSuccessPhenotypeV2TraitsForResult = ({
  data,
  controlVariableDefinitionsMapping,
}: {
  data: SuccessPhenotypeV2Result;
  controlVariableDefinitionsMapping: ControlVariableDefinitionsMapping;
}): Trait[] => {
  const traitsRaw = data?.traits ?? [];
  return traitsRaw
    .reduce((acc: Trait[], trait) => {
      const levelsWithNonZeroRoundedPercentage = trait.levels.filter(
        (level) => {
          const controlVariableDefinition =
            controlVariableDefinitionsMapping[
              trait.controlVariableColumnHeaderValue
            ];

          const roundedPercentage =
            controlVariableDefinition.numericalTraitDefinition
              ? roundToDenominator({
                  value: level.percentage,
                  denominator:
                    controlVariableDefinition.numericalTraitDefinition
                      .denominator,
                })
              : level.percentage;

          return roundedPercentage !== 0;
        },
      );

      const hasAtLeastOneNonZeroTraitLevel =
        levelsWithNonZeroRoundedPercentage.length > 0;

      if (hasAtLeastOneNonZeroTraitLevel) {
        acc.push({
          ...trait,
          levels: levelsWithNonZeroRoundedPercentage,
          // cast is to please TS since the levels isn't directly from the trait and it's not sure if the levels union matches the trait type
        } as Trait);
      }

      return acc;
    }, [])
    .slice(0, 6);
};

export const truncateTextIfNecessary = ({
  text,
  maxChars,
}: {
  text: string;
  maxChars: number;
}): string => {
  if (text.length > maxChars) {
    return text.slice(0, maxChars) + '...';
  }
  return text;
};

export const DEFAULT_UNITS = {
  singular: 'unit',
  plural: 'units',
};

/**
 * Rounds a number to the nearest multiple of a given denominator. If the values is less than 1 and the demominator is 1 or greater, it will show two decimal places so we can avoid showing 0 instead of the small float.
 * @example roundToDenominator({ value: 0.5, denominator: 1 }) // => 1
 * @example roundToDenominator({ value: 0.5, denominator: 0.5 }) // => 0.5
 */
export const roundToDenominator = ({
  value,
  denominator,
}: {
  value: number;
  denominator: number;
}): number => {
  // Always show a float with two decimal places if the value is less than 1 and the denominator is 1 or greater
  if (value < 1 && denominator >= 1) {
    return parseFloat(value.toFixed(2));
  }

  // Determine the number of decimal places in the denominator
  const denominatorString = denominator.toString();
  const decimalPlaces = denominatorString.includes('.')
    ? denominatorString.split('.')[1].length
    : 0;

  // Round the value to the nearest denominator
  const scaledValue = Math.round(value / denominator) * denominator;

  // Adjust rounding to match the number of decimal places in the denominator
  const factor = Math.pow(10, decimalPlaces);
  return Math.round(scaledValue * factor) / factor;
};

export const formatNumericalTwoSidedTraitLevelValue = ({
  traitDefinition,
  traitLevel,
  withoutUnit,
  ignoreRounding,
}: {
  traitDefinition: NumericalTraitDefinition;
  traitLevel: NumericalTwoSidedTraitLevel;
  withoutUnit?: boolean;
  ignoreRounding?: boolean;
}): string => {
  // Apply unit scoring transformation if configured
  if (traitDefinition.unitScoring) {
    const fromValue =
      (traitLevel.fromValue / traitDefinition.unitScoring.actualMaximumValue) *
      traitDefinition.unitScoring.displayMaximumValue;
    const toValue =
      (traitLevel.toValue / traitDefinition.unitScoring.actualMaximumValue) *
      traitDefinition.unitScoring.displayMaximumValue;

    // For scores, we never completely ignore rounding since it will look weird if it is a really long decimal place.
    const firstPart = roundToDenominator({
      value: fromValue,
      denominator: ignoreRounding ? 0.01 : traitDefinition.denominator,
    });
    const secondPart = roundToDenominator({
      value: toValue,
      denominator: ignoreRounding ? 0.01 : traitDefinition.denominator,
    });

    const displayMax = traitDefinition.unitScoring.displayMaximumValue;

    if (withoutUnit) {
      return `${firstPart}/${displayMax}-${secondPart}/${displayMax}`;
    }

    const unitPlural = traitDefinition.unitPlural ?? DEFAULT_UNITS.plural;
    return `${firstPart}/${displayMax}-${secondPart}/${displayMax} ${unitPlural}`;
  }

  // No unit scoring - use original behavior
  const firstPart = ignoreRounding
    ? traitLevel.fromValue
    : roundToDenominator({
        value: traitLevel.fromValue,
        denominator: traitDefinition.denominator,
      });
  const secondPart = ignoreRounding
    ? traitLevel.toValue
    : roundToDenominator({
        value: traitLevel.toValue,
        denominator: traitDefinition.denominator,
      });
  const valueWithoutUnit = `${firstPart}-${secondPart}`;

  if (withoutUnit) {
    return valueWithoutUnit;
  }

  if (traitLevel.toValue === 1) {
    const unitSingular = traitDefinition.unitSingular ?? DEFAULT_UNITS.singular;

    return `${valueWithoutUnit} ${unitSingular}`;
  }

  const unitPlural = traitDefinition.unitPlural ?? DEFAULT_UNITS.plural;

  return `${valueWithoutUnit} ${unitPlural}`;
};

export const formatNumericalOneSidedTraitLevelValue = ({
  traitDefinition,
  traitLevel,
  withoutUnit,
  ignoreRounding,
}: {
  traitDefinition: NumericalTraitDefinition;
  traitLevel: NumericalOneSidedTraitLevel;
  withoutUnit?: boolean;
  ignoreRounding?: boolean;
}): string => {
  // Apply unit scoring transformation if configured
  if (traitDefinition.unitScoring) {
    const value =
      (traitLevel.value / traitDefinition.unitScoring.actualMaximumValue) *
      traitDefinition.unitScoring.displayMaximumValue;

    // For scores, we never completely ignore rounding since it will look weird if it is a really long decimal place.
    const formattedValue = roundToDenominator({
      value: value,
      denominator: ignoreRounding ? 0.01 : traitDefinition.denominator,
    });

    const prefix = traitLevel.type === 'greaterThan' ? '>' : '≤';
    const displayMax = traitDefinition.unitScoring.displayMaximumValue;

    if (withoutUnit) {
      return `${prefix}${formattedValue}/${displayMax}`;
    }

    const unitPlural = traitDefinition.unitPlural ?? DEFAULT_UNITS.plural;
    return `${prefix}${formattedValue}/${displayMax} ${unitPlural}`;
  }

  // No unit scoring - use original behavior
  const value = ignoreRounding
    ? traitLevel.value
    : roundToDenominator({
        value: traitLevel.value,
        denominator: traitDefinition.denominator,
      });

  const prefix = traitLevel.type === 'greaterThan' ? '>' : '≤';

  const valueWithoutUnit = `${prefix}${value}`;

  if (withoutUnit) {
    return valueWithoutUnit;
  }

  if (traitLevel.value === 1) {
    const unitSingular = traitDefinition.unitSingular ?? DEFAULT_UNITS.singular;

    return `${valueWithoutUnit} ${unitSingular}`;
  }

  const unitPlural = traitDefinition.unitPlural ?? DEFAULT_UNITS.plural;

  return `${valueWithoutUnit} ${unitPlural}`;
};

/**
 * Formats a numerical trait level value with the appropriate unit pluralization and prefix/suffix
 *
 * @example formatNumericalTraitLevelValue({ trait: { unitSingular: 'year', unitPlural: 'years' }, traitLevel: { value: 1, type: 'greaterThan' } }) // => '+1 year'
 * @example formatNumericalTraitLevelValue({ trait: { unitSingular: 'year', unitPlural: 'years' }, traitLevel: { value: 2, type: 'lessThanOrEqual' } }) // => '<=2 years'
 */
export const formatNumericalTraitLevelValue = ({
  traitDefinition,
  traitLevel,
  withoutUnit,
  ignoreRounding,
}: {
  traitDefinition: NumericalTraitDefinition;
  traitLevel: NumericalTraitLevel;
  withoutUnit?: boolean;
  ignoreRounding?: boolean;
}): string => {
  if (traitLevel.type === 'twoSided') {
    return formatNumericalTwoSidedTraitLevelValue({
      traitDefinition,
      // This as cast only exists due to a strange bug in pydantic.
      traitLevel: traitLevel,
      withoutUnit,
      ignoreRounding,
    });
  } else {
    return formatNumericalOneSidedTraitLevelValue({
      traitDefinition,
      // This as cast only exists due to a strange bug in pydantic.
      traitLevel: traitLevel,
      withoutUnit,
      ignoreRounding,
    });
  }
};

/**
 * Formats both categorical and numerical trait level values
 */
export const formatTraitLevelValue = ({
  controlVariableDefinition,
  traitLevel,
  withoutUnit,
  ignoreRounding,
}: {
  controlVariableDefinition: ControlVariableDefinition;
  traitLevel: TraitLevel;
  withoutUnit?: boolean;
  ignoreRounding?: boolean;
}): string => {
  if (
    controlVariableDefinition.dataType === 'numeric' &&
    controlVariableDefinition.numericalTraitDefinition
  ) {
    return formatNumericalTraitLevelValue({
      traitDefinition: controlVariableDefinition.numericalTraitDefinition,
      traitLevel: traitLevel as NumericalTraitLevel,
      withoutUnit,
      ignoreRounding,
    });
  }

  if (isCategoricalTraitLevel(traitLevel)) {
    return traitLevel.value;
  }

  // This won't happen unless it's wrongly configured somehow outside of the admin panel
  return '-';
};

export const getSuccessPhenotypeV2SelectedMetric = ({
  metric,
  myObservation,
}: {
  metric: string;
  myObservation: MyObservation | null;
}) => {
  return getMetricDisplayNameFromObservation({
    focusArea: 'Performance',
    metricName: metric,
    myObservation,
  });
};

// Return color for data based on value
export const getBarColor = (value: number) => {
  // ≤0 and <40%: royal or red 300
  // ≤40 and <50%: royal or red 400
  // ≤50 and <60%: royal or red 500
  // ≤60 and <70%: royal or red 600
  // ≤70 and <80%: royal or red 700
  // ≤80 and <90%: royal or red 800
  // ≤90%: royal or red 900
  if (value >= 90) {
    return '#0E2467';
  } else if (value >= 80) {
    return '#11349B';
  } else if (value >= 70) {
    return '#1B47C9';
  } else if (value >= 60) {
    return '#3460E2';
  } else if (value >= 50) {
    return '#547BEE';
  } else if (value >= 40) {
    return '#809EF4';
  } else if (value > 0) {
    return '#A1B8F7';
  } else if (value === 0) {
    return '#FFF5F5';
  } else if (value > -40) {
    return '#F8A0A0';
  } else if (value > -50) {
    return '#F27C7C';
  } else if (value > -60) {
    return '#EE3F3F';
  } else if (value > -70) {
    return '#D41515';
  } else if (value > -80) {
    return '#AE0C0C';
  } else if (value > -90) {
    return '#820808';
  } else {
    return '#5C0808';
  }
};

export const traitToTraitLevelDatums = ({
  activeTrait,
  controlVariableDefinitionsMapping,
  withoutUnit,
  ignoreRoundingTraitLevelValue,
}: {
  activeTrait: Trait;
  controlVariableDefinitionsMapping: ControlVariableDefinitionsMapping;
  withoutUnit?: boolean;
  /**
   * We use this for key factors, since truncating the value will lead to confusion in the generated explanations.
   */
  ignoreRoundingTraitLevelValue?: boolean;
}): TraitLevelBarChartDatum[] => {
  const levelsTyped: (NumericalTraitLevel | CategoricalTraitLevel)[] =
    activeTrait.levels;
  return levelsTyped
    .map((level, originalLevelIndex) => {
      const controlVariableDefinition =
        controlVariableDefinitionsMapping[
          activeTrait.controlVariableColumnHeaderValue
        ];

      const id = formatTraitLevelValue({
        controlVariableDefinition,
        traitLevel: level,
        withoutUnit,
        ignoreRounding: ignoreRoundingTraitLevelValue,
      });

      const roundedPercentage =
        controlVariableDefinition.numericalTraitDefinition
          ? roundToDenominator({
              value: level.percentage,
              denominator:
                controlVariableDefinition.numericalTraitDefinition.denominator,
            })
          : level.percentage;

      return {
        id,
        value: roundedPercentage,
        valueColor: getBarColor(level.percentage),
        // These extra values are used by the key factors helper for ordering etc.
        rawPercentage: level.percentage,
        originalIndex: originalLevelIndex,
        originalLevel: level,
      };
    })
    .sort((a, b) => {
      return b.rawPercentage - a.rawPercentage;
    })
    .slice(0, 8);
};

export const traitToTraitLevelDatumsForKeyFactors = ({
  activeTrait,
  controlVariableDefinitionsMapping,
  withoutUnit,
}: {
  activeTrait: Trait;
  controlVariableDefinitionsMapping: ControlVariableDefinitionsMapping;
  withoutUnit?: boolean;
}): {
  datums: TraitLevelBarChartDatum[];
  bestTraitDatumIndex: number | null;
} => {
  const datums = traitToTraitLevelDatums({
    activeTrait,
    controlVariableDefinitionsMapping,
    withoutUnit,
    ignoreRoundingTraitLevelValue: true,
  });

  const sortedDatums: TraitLevelBarChartDatum[] = [];

  const controlVariableDefinition =
    controlVariableDefinitionsMapping[
      activeTrait.controlVariableColumnHeaderValue
    ];

  if (activeTrait.type === 'categorical') {
    // the value itself is the thing we want to sort on
    const sortedLevels =
      sortWithReferenceOrderPlacingUnknownsLastAlphabetically({
        items: datums.map((datum) => datum.id),
        referenceOrder:
          controlVariableDefinition?.categoricalTraitDefinition?.sortOrder ??
          [],
      });

    for (const level of sortedLevels) {
      const datum = datums.find((d) => d.id === level);
      if (datum) {
        sortedDatums.push(datum);
      }
    }
  } else {
    const originalLevels = datums
      .map((datum) => datum.originalLevel)
      .filter((level): level is NumericalTraitLevel => level !== null);

    const sortedLevels = sortNumericalTraitLevels(originalLevels);

    for (const level of sortedLevels) {
      const datum = datums.find((d) => d.originalLevel === level);
      if (datum) {
        sortedDatums.push(datum);
      }
    }
  }

  // The best one has the highest raw percentage
  let bestTraitDatumIndex: number | null = null;
  let bestRawPercentage = Number.NEGATIVE_INFINITY;
  sortedDatums.forEach((datum, index) => {
    if (datum.rawPercentage > bestRawPercentage) {
      bestRawPercentage = datum.rawPercentage;
      bestTraitDatumIndex = index;
    }
  });

  return {
    datums: sortedDatums,
    bestTraitDatumIndex,
  };
};

const typeRank = (t: NumericalTraitLevel['type']): number =>
  t === 'lessThanOrEqual' ? 0 : t === 'twoSided' ? 1 : 2;

const leftEdge = (l: NumericalTraitLevel): number =>
  l.type === 'twoSided' ? l.fromValue : l.value;

// Used only as a final tie-breaker when leftEdge and typeRank are equal
const secondary = (l: NumericalTraitLevel): number =>
  l.type === 'twoSided'
    ? l.toValue
    : l.type === 'lessThanOrEqual'
      ? Number.NEGATIVE_INFINITY
      : Number.POSITIVE_INFINITY;

export const compareNumericalTraitLevel = (
  a: NumericalTraitLevel,
  b: NumericalTraitLevel,
): number => {
  const ka: [number, number, number] = [
    leftEdge(a),
    typeRank(a.type),
    secondary(a),
  ];
  const kb: [number, number, number] = [
    leftEdge(b),
    typeRank(b.type),
    secondary(b),
  ];

  // Lexicographic compare without subtraction (handles ±Infinity safely)
  for (let i = 0; i < 3; i++) {
    if (ka[i] < kb[i]) return -1;
    if (ka[i] > kb[i]) return 1;
  }
  return 0;
};

export const sortNumericalTraitLevels = <T extends NumericalTraitLevel>(
  levels: T[],
): T[] => [...levels].sort(compareNumericalTraitLevel);

export const getSuccessPhenotypeV2DataWithExplanations = () => {
  return {
    pollStatus: 'complete',
    createdAt: Date.now(),
    outputContents:
      '{"fullSampleSize": 444, "dataQualityScore": 1.0, "traits":[{"type":"categorical","controlVariableColumnHeaderValue":"education","levels":[{"type":"equal","value":"No degree","percentage":21},{"type":"equal","value":"Professional degree","percentage":2},{"type":"equal","value":"Associate\'s degree","percentage":-5},{"type":"equal","value":"Doctoral degree","percentage":-7.000000000000001}],"sampleSize":148},{"type":"numeric","controlVariableColumnHeaderValue":"experience","levels":[{"type":"greaterThan","value":37,"percentage":19}],"sampleSize":148},{"type":"numeric","controlVariableColumnHeaderValue":"tenure","levels":[{"type":"greaterThan","value":1.2,"percentage":18},{"type":"twoSided","fromValue":0.86,"toValue":1.2,"percentage":-4}],"sampleSize":148}],"explanations":{"sf_breakdown/education":{"overview":"On average, the optimal education for an employee is at No degree, holding other factors constant. These employees have a 21% likelihood of task completion.","description":"Other employees also likely to have task completion include at Professional degree (2%). Other employees also less likely to have task completion include at Associate\'s degree (-5%), and at Doctoral degree (-7%).","positiveDescription":null,"negativeDescription":null},"sf_breakdown/experience":{"overview":"On average, the optimal experience for an employee is over 37, holding other factors constant. These employees have a 19% likelihood of task completion.","description":"","positiveDescription":null,"negativeDescription":null},"sf_breakdown/tenure":{"overview":"On average, the optimal tenure for an employee is 1 or more, holding other factors constant. These employees have an 18% likelihood of task completion.","description":"Other employees also less likely to have task completion include tenure at 1 (-4%).","positiveDescription":null,"negativeDescription":null},"sf_example/education":{"overview":"To build stronger teams, prioritize people with a Doctoral degree. Person A has a Doctoral degree, while Person B has no degree. Person A is less likely and Person B is likely to complete tasks at your company, however, Person A is 28% less likely to complete tasks compared to Person B.","description":null,"positiveDescription":null,"negativeDescription":null},"sf_example/experience":{"overview":"To build stronger teams, prioritize people with experience 37 or more, as they have a higher likelihood of task completion.","description":null,"positiveDescription":null,"negativeDescription":null},"sf_example/tenure":{"overview":"Person A has tenure of 1, while Person B has tenure of 1 or more. Person A is less likely and Person B is likely to complete tasks at your company, however, Person A is 22% less likely to complete tasks compared to Person B.","description":null,"positiveDescription":null,"negativeDescription":null},"sf_optimal_rule/education":{"overview":"This control variable has limited impact on task completion.","description":null,"positiveDescription":null,"negativeDescription":null},"sf_optimal_rule/experience":{"overview":"To build stronger teams, prioritize people with 37 or more experience, as they have a 19% likelihood of task completion.","description":null,"positiveDescription":null,"negativeDescription":null},"sf_optimal_rule/tenure":{"overview":"This control variable has limited impact on task completion.","description":null,"positiveDescription":null,"negativeDescription":null},"sf_rule/education/0":{"overview":"Employees with education No degree are 21% more likely to have higher task completion (holding all other factors constant).","description":null,"positiveDescription":null,"negativeDescription":null},"sf_rule/education/1":{"overview":"Employees with a Professional degree education are 2% more likely to have higher task completion (holding all other factors constant).","description":null,"positiveDescription":null,"negativeDescription":null},"sf_rule/education/2":{"overview":"Employees with an education of Associate\'s degree are 5% less likely to have higher task completion (holding all other factors constant).","description":null,"positiveDescription":null,"negativeDescription":null},"sf_rule/education/3":{"overview":"Employees with a Doctoral degree are 7% less likely to have higher task completion (holding all other factors constant).","description":null,"positiveDescription":null,"negativeDescription":null},"sf_rule/experience/0":{"overview":"Employees with experience 37 or more are 19% more likely to have higher task completion (holding all other factors constant).","description":null,"positiveDescription":null,"negativeDescription":null},"sf_rule/tenure/0":{"overview":"Employees with a tenure of 1 or more are 18% more likely to have higher task completion (holding all other factors constant).","description":null,"positiveDescription":null,"negativeDescription":null},"sf_rule/tenure/1":{"overview":"Employees with a tenure of exactly 1 year are 4% less likely to successfully complete tasks (holding all other factors constant).","description":null,"positiveDescription":null,"negativeDescription":null}}}',
  } satisfies CustomVizPollCompleteResponse;
};

export const getRuleOutputExplanationForKeyFactors = ({
  explanations,
  originalLevelIndex,
  trait,
  selectedGroupLabel,
  selectedGroupLevelValues,
  metric,
  myObservation,
}: {
  explanations: ExplanationsOutputDictionary | undefined;
  originalLevelIndex: number | null;
  trait: Trait;
  selectedGroupLabel: string;
  selectedGroupLevelValues: number[];
  metric: MetricDefinition | undefined;
  myObservation: MyObservation;
}) => {
  const subjectPlural = upperFirst(
    metric?.whiteLabeling?.subjectPlural ?? 'employees',
  );

  const controlVariableDefinitionsMapping =
    getSuccessPhenotypeV2ControlVariableDefinitionsMappingFromObservation({
      myObservation,
    });

  const controlVariableDefinition =
    controlVariableDefinitionsMapping[trait.controlVariableColumnHeaderValue];

  const fallbackText = `${selectedGroupLabel} ${
    controlVariableDefinition.numericalTraitDefinition
      ? `${controlVariableDefinition.numericalTraitDefinition.unitPlural} of `
      : ''
  }${controlVariableDefinition.displayName.toLowerCase()}: ${subjectPlural} with ${selectedGroupLabel} ${
    controlVariableDefinition.numericalTraitDefinition
      ? `${controlVariableDefinition.numericalTraitDefinition.unitPlural} of `
      : ''
  }${controlVariableDefinition.displayName.toLowerCase()} are ${selectedGroupLevelValues[0]}% more likely to have a higher ${metric?.displayName.toLowerCase()}.`;

  const ruleKey =
    originalLevelIndex === null
      ? ''
      : getKeyFactorsRuleOutputKey({
          traitName: trait.controlVariableColumnHeaderValue,
          levelIndex: originalLevelIndex,
        });
  const rule = explanations?.[ruleKey];

  const ruleOverview = rule?.overview ?? '';

  return {
    overview: ruleOverview.trim() === '' ? fallbackText : ruleOverview,
  };
};

export const getExampleExplanationForKeyFactors = ({
  explanations,
  trait,
}: {
  explanations: ExplanationsOutputDictionary | undefined;
  trait: Trait;
}) => {
  const ruleKey = getKeyFactorsExampleOutputKey({
    traitName: trait.controlVariableColumnHeaderValue,
  });
  const rule = explanations?.[ruleKey];

  const ruleOverview = rule?.overview ?? '';

  return {
    overview: ruleOverview.trim() === '' ? '' : ruleOverview,
  };
};

export const getBreakdownExplanationForKeyFactors = ({
  explanations,
  trait,
  language,
  myObservation,
  targetRange,
  metric,
}: {
  explanations: ExplanationsOutputDictionary | undefined;
  trait: Trait;
  language: Phrases;
  myObservation: MyObservation;
  targetRange: string;
  metric: string;
}) => {
  const controlVariableDefinitionsMapping =
    getSuccessPhenotypeV2ControlVariableDefinitionsMappingFromObservation({
      myObservation,
    });

  const controlVariableDefinition =
    controlVariableDefinitionsMapping[trait.controlVariableColumnHeaderValue];

  const controlVariableDisplayName =
    controlVariableDefinition?.displayName ??
    trait.controlVariableColumnHeaderValue;

  const metricDefinition = getMetricByColumnHeaderValueFromObservation({
    columnHeaderValue: metric,
    myObservation,
  });

  const metricDisplayName = metricDefinition?.displayName ?? metric;

  const metricCalibrationFraming =
    metricDefinition?.calibrationFraming === 'Negative'
      ? 'Negative'
      : 'Positive';

  const ruleKey = getKeyFactorsBreakdownOutputKey({
    traitName: trait.controlVariableColumnHeaderValue,
  });
  const rule = explanations?.[ruleKey];

  const fallbackText = language.keyFactorsBreakdownFallback({
    controlVariableDisplayName,
    metricCalibrationFraming,
    metricDisplayName,
    targetRange,
  });

  const ruleOverview = rule?.overview ?? fallbackText;

  return {
    overview: ruleOverview,
  };
};

export const getOptimalRuleExplanationForKeyFactors = ({
  explanations,
  trait,
  language,
  metric,
  myObservation,
  targetRange,
}: {
  explanations: ExplanationsOutputDictionary | undefined;
  trait: Trait;
  language: Phrases;
  metric: string;
  myObservation: MyObservation;
  targetRange: string;
}) => {
  const metricDefinition = getMetricByColumnHeaderValueFromObservation({
    columnHeaderValue: metric,
    myObservation,
  });

  const metricDisplayName = metricDefinition?.displayName ?? metric;

  const controlVariableDefinitionsMapping =
    getSuccessPhenotypeV2ControlVariableDefinitionsMappingFromObservation({
      myObservation,
    });

  const controlVariableDefinition =
    controlVariableDefinitionsMapping[trait.controlVariableColumnHeaderValue];

  const fallbackText = language.keyFactorsOptimalRuleFallback({
    metricDisplayName,
    controlVariableDisplayName:
      controlVariableDefinition?.displayName ??
      trait.controlVariableColumnHeaderValue,
    targetRange,
  });

  const ruleKey = getKeyFactorsOptimalRuleOutputKey({
    traitName: trait.controlVariableColumnHeaderValue,
  });
  const rule = explanations?.[ruleKey];

  const ruleOverview = rule?.overview ?? '';

  return {
    overview: ruleOverview.trim() === '' ? fallbackText : ruleOverview,
  };
};

export const getTargetRangeDisplayForKeyFactors = ({
  myObservation,
  trait,
}: {
  myObservation: MyObservation;
  trait: Trait;
}) => {
  if (!myObservation) {
    return '';
  }

  const controlVariableDefinitionsMapping =
    getSuccessPhenotypeV2ControlVariableDefinitionsMappingFromObservation({
      myObservation,
    });

  const { datums: traitDatum, bestTraitDatumIndex } =
    traitToTraitLevelDatumsForKeyFactors({
      controlVariableDefinitionsMapping,
      activeTrait: trait,
    });

  return bestTraitDatumIndex === null ? '' : traitDatum[bestTraitDatumIndex].id;
};
